import React from "react";
import { Box, keyframes } from "@mui/material";
import { SIGNUP_FONT } from "../utils/signupPageStyles";
import { BRAND_PRIMARY } from "../constants/brandColors";
import { getMm } from "../theme/modernMaterialEnterprise";

const slideInForward = keyframes`
  from { opacity: 0; transform: translateX(16px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInBack = keyframes`
  from { opacity: 0; transform: translateX(-16px); }
  to { opacity: 1; transform: translateX(0); }
`;

export function SignupStepIndicator({ steps, activeIndex, isDarkMode }) {
  const divider = getMm(isDarkMode).outlineVariant;

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: { xs: 0.5, sm: 0.75 },
          width: "100%",
          maxWidth: 360,
          mx: "auto",
        }}
      >
        {steps.map((label, idx) => {
          const isComplete = idx < activeIndex;
          const isActive = idx === activeIndex;
          return (
            <React.Fragment key={label}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "8px",
                  border: "2px solid",
                  borderColor: isComplete || isActive ? BRAND_PRIMARY.main : divider,
                  backgroundColor: isComplete
                    ? BRAND_PRIMARY.main
                    : isActive
                      ? getMm(isDarkMode).surfaceLowest
                      : getMm(isDarkMode).surfaceLow,
                  color: isComplete
                    ? "#FFFFFF"
                    : isActive
                      ? getMm(isDarkMode).onSurface
                      : getMm(isDarkMode).onSurfaceVariant,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: "0.8125rem",
                  fontFamily: SIGNUP_FONT,
                  flexShrink: 0,
                }}
              >
                {idx + 1}
              </Box>
              {idx < steps.length - 1 ? (
                <Box
                  sx={{
                    flex: { xs: 1, sm: "0 0 auto" },
                    width: { xs: "auto", sm: 28 },
                    minWidth: { xs: 12, sm: 28 },
                    maxWidth: { xs: 40, sm: 28 },
                    height: 2,
                    borderRadius: 1,
                    backgroundColor: idx < activeIndex ? BRAND_PRIMARY.main : divider,
                  }}
                />
              ) : null}
            </React.Fragment>
          );
        })}
      </Box>
      <TypographyStepLabel steps={steps} activeIndex={activeIndex} isDarkMode={isDarkMode} />
    </Box>
  );
}

function TypographyStepLabel({ steps, activeIndex, isDarkMode }) {
  return (
    <Box
      component="p"
      sx={{
        m: 0,
        mt: 1.25,
        textAlign: "center",
        fontSize: { xs: "0.75rem", sm: "0.8125rem" },
        fontWeight: 600,
        color: getMm(isDarkMode).onSurfaceVariant,
        fontFamily: SIGNUP_FONT,
        px: 1,
        wordBreak: "break-word",
      }}
    >
      {steps[activeIndex]}
    </Box>
  );
}

export default function SignupStepTransition({ stepKey, direction, children }) {
  return (
    <Box
      key={stepKey}
      sx={{
        animation: `${direction === "forward" ? slideInForward : slideInBack} 0.34s ease-out`,
        "@media (prefers-reduced-motion: reduce)": {
          animation: "none",
        },
      }}
    >
      {children}
    </Box>
  );
}
